<template>
  <a-drawer title="发放优惠券 - 全景套餐" :visible="visible" :width="1000" @close="close" destroyOnClose>
    <!-- 发放数量配置 - 放在表格上方 -->
    <div class="distribute-config">
      <a-form layout="inline" :label-col="{ span: 8 }">
        <a-form-item label="发放数量" required>
          <a-input-number
            v-model="distributeCount"
            :min="1"
            :max="1000"
            :precision="0"
            placeholder="请输入发放张数"
            style="width: 120px"
          />
          <span style="margin-left: 8px; color: #666">张/人</span>
        </a-form-item>
        <a-form-item>
          <span class="distribution-hint">每个选中的用户将获得指定数量的优惠券</span>
        </a-form-item>
      </a-form>
    </div>

    <!-- Tabs 切换 -->
    <a-tabs v-model="activeTab" @change="onTabChange" style="margin-top: 16px">
      <!-- 企业 Tab -->
      <a-tab-pane key="enterprise" tab="企业">
        <customer-table
          type="enterprise"
          :query-param="enterpriseQueryParam"
          :selected-keys="enterpriseSelectedRowKeys"
          :loading="enterpriseLoading"
          :data-source="enterpriseList"
          :pagination="enterprisePagination"
          @query="handleEnterpriseQuery"
          @reset="resetEnterpriseQuery"
          @table-change="handleEnterpriseTableChange"
          @selection-change="handleEnterpriseSelectionChange"
        />
      </a-tab-pane>

      <!-- 代理商 Tab -->
      <a-tab-pane key="agent" tab="代理商">
        <customer-table
          type="agent"
          :query-param="agentQueryParam"
          :selected-keys="agentSelectedRowKeys"
          :loading="agentLoading"
          :data-source="agentList"
          :pagination="agentPagination"
          @query="handleAgentQuery"
          @reset="resetAgentQuery"
          @table-change="handleAgentTableChange"
          @selection-change="handleAgentSelectionChange"
        />
      </a-tab-pane>
    </a-tabs>

    <!-- 底部操作按钮和统计信息 -->
    <div class="drawer-footer">
      <div class="footer-left">
        <div class="distribution-summary">
          <div class="summary-item">
            <span class="label">选中的用户:</span>
            <span class="value">{{ selectedUserCount }} 个</span>
          </div>
          <div class="summary-item">
            <span class="label">发放总数:</span>
            <span class="value">{{ totalDistributionCount }} 张</span>
          </div>
        </div>
      </div>
      <div class="footer-right">
        <a-button style="margin-right: 8px" @click="close">取消</a-button>
        <a-button
          type="primary"
          :loading="submitting"
          @click="submit"
          :disabled="!canSubmit"
        >
          发放
        </a-button>
      </div>
    </div>
  </a-drawer>
</template>

<script>
import { fetchEnterpriseList, fetchAgentList } from '@/api/declaration/customer'
import { distributeCoupon } from '@/api/coupon/controller'

// 基础的查询参数配置 - 包含所有必需参数
const BASE_ENTERPRISE_QUERY = {
  pageNum: 1,
  pageSize: 10,
  type: 1, // 企业类型
  // status: 0, // 必需的状态参数
  name: '',
  contacts: '',
}

const BASE_AGENT_QUERY = {
  pageNum: 1,
  pageSize: 10,
  type: 2, // 代理商类型
  // status: 0, // 必需的状态参数
  name: '',
  contacts: '',
}

// 提取公共的表格组件
const CustomerTable = {
  props: {
    type: String,
    queryParam: Object,
    selectedKeys: Array,
    loading: Boolean,
    dataSource: Array,
    pagination: Object,
  },
  computed: {
    columns() {
      const baseColumns = [
        {
          title: this.type === 'enterprise' ? '企业名称' : '代理商名称',
          dataIndex: 'name',
          align: 'center',
          ellipsis: true,
        },
        {
          title: '联系人',
          dataIndex: 'contacts',
          align: 'center',
        },
        {
          title: '联系电话',
          dataIndex: 'mobile',
          align: 'center',
        },
      ]

      if (this.type === 'enterprise') {
        baseColumns.push({
          title: '企业地址',
          dataIndex: 'address',
          align: 'center',
          ellipsis: true,
        })
      } else {
        baseColumns.push({
          title: '状态',
          dataIndex: 'status',
          align: 'center',
          customRender: (text) => (text === 0 ? '正常' : '停用'),
        })
      }

      return baseColumns
    },
    rowSelection() {
      return {
        selectedRowKeys: this.selectedKeys,
        onChange: (selectedRowKeys) => {
          this.$emit('selection-change', selectedRowKeys)
        },
        type: 'checkbox',
      }
    },
  },
  methods: {
    handleQuery() {
      this.$emit('query')
    },
    handleReset() {
      this.$emit('reset')
    },
    handleTableChange(pagination) {
      this.$emit('table-change', pagination)
    },
  },
  template: `
    <div>
      <a-form layout="inline" style="margin-bottom: 16px">
        <a-form-item :label="type === 'enterprise' ? '企业名称' : '代理商名称'">
          <a-input v-model="queryParam.name" :placeholder="\`请输入\${type === 'enterprise' ? '企业' : '代理商'}名称\`" />
        </a-form-item>
        <a-form-item label="联系人">
          <a-input v-model="queryParam.contacts" placeholder="请输入联系人" />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="handleQuery">查询</a-button>
          <a-button style="margin-left: 8px" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>

      <a-table
        rowKey="id"
        :columns="columns"
        :data-source="dataSource"
        :pagination="pagination"
        :row-selection="rowSelection"
        :loading="loading"
        @change="handleTableChange"
      />
    </div>
  `,
}

export default {
  name: 'PanoramaCouponDistributeDrawer',
  components: {
    CustomerTable,
  },
  data() {
    return {
      visible: false,
      couponId: null,
      activeTab: 'enterprise',
      submitting: false,
      distributeCount: 1, // 默认发放1张

      // 缓存已加载的数据，避免重复请求
      dataLoaded: {
        enterprise: false,
        agent: false,
      },

      // 企业相关数据
      enterpriseList: [],
      enterpriseLoading: false,
      enterpriseSelectedRowKeys: [],
      enterpriseQueryParam: { ...BASE_ENTERPRISE_QUERY },
      enterprisePagination: {
        current: 1,
        total: 0,
        pageSize: 10,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: total => `共 ${total} 条`,
      },

      // 代理商相关数据
      agentList: [],
      agentLoading: false,
      agentSelectedRowKeys: [],
      agentQueryParam: { ...BASE_AGENT_QUERY },
      agentPagination: {
        current: 1,
        total: 0,
        pageSize: 10,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: total => `共 ${total} 条`,
      },
    }
  },
  computed: {
    // 获取当前选中的总用户ID列表
    selectedUserIds() {
      return this.activeTab === 'enterprise'
        ? this.enterpriseSelectedRowKeys
        : this.agentSelectedRowKeys
    },
    // 获取用户类型
    selectedUserType() {
      return this.activeTab === 'enterprise' ? 'enterprise' : 'agent'
    },
    // 选中的用户数量
    selectedUserCount() {
      return this.selectedUserIds.length
    },
    // 发放总数
    totalDistributionCount() {
      return this.selectedUserCount * this.distributeCount
    },
    // 是否可以提交
    canSubmit() {
      return this.selectedUserCount > 0 && this.distributeCount > 0
    },
  },
  methods: {
    open(couponId) {
      this.couponId = couponId
      this.visible = true
      // 只加载当前激活的tab数据
      this.loadCurrentTabData()
    },
    close() {
      this.visible = false
      this.activeTab = 'enterprise'
      this.enterpriseSelectedRowKeys = []
      this.agentSelectedRowKeys = []
      this.distributeCount = 1
      this.dataLoaded.enterprise = false
      this.dataLoaded.agent = false

      // 重置查询参数到初始状态（包含所有必需参数）
      this.enterpriseQueryParam = { ...BASE_ENTERPRISE_QUERY }
      this.agentQueryParam = { ...BASE_AGENT_QUERY }

      // 重置分页
      this.enterprisePagination.current = 1
      this.agentPagination.current = 1
      this.enterprisePagination.total = 0
      this.agentPagination.total = 0
    },
    // Tab 切换处理 - 优化为按需加载
    onTabChange(activeKey) {
      this.activeTab = activeKey
      this.loadCurrentTabData()
    },
    // 按需加载当前tab数据
    loadCurrentTabData() {
      if (this.activeTab === 'enterprise' && !this.dataLoaded.enterprise) {
        this.getEnterpriseList()
        this.dataLoaded.enterprise = true
      } else if (this.activeTab === 'agent' && !this.dataLoaded.agent) {
        this.getAgentList()
        this.dataLoaded.agent = true
      }
    },
    // 企业相关方法
    getEnterpriseList() {
      this.enterpriseLoading = true
      fetchEnterpriseList(this.enterpriseQueryParam)
        .then((res) => {
          this.enterpriseList = res.data.records || []
          this.enterprisePagination.total = res.data.total || 0
          this.enterprisePagination.current = this.enterpriseQueryParam.pageNum
        })
        .finally(() => {
          this.enterpriseLoading = false
        })
    },
    handleEnterpriseQuery() {
      this.enterpriseQueryParam.pageNum = 1
      this.enterprisePagination.current = 1
      this.getEnterpriseList()
    },
    resetEnterpriseQuery() {
      // 重置为企业的基础查询参数，保留必要的type和status
      this.enterpriseQueryParam = { ...BASE_ENTERPRISE_QUERY }
      this.enterprisePagination.current = 1
      this.getEnterpriseList()
    },
    handleEnterpriseTableChange(pagination) {
      this.enterpriseQueryParam.pageNum = pagination.current
      this.enterpriseQueryParam.pageSize = pagination.pageSize
      this.enterprisePagination = {
        ...this.enterprisePagination,
        current: pagination.current,
        pageSize: pagination.pageSize,
      }
      this.getEnterpriseList()
    },
    handleEnterpriseSelectionChange(selectedRowKeys) {
      this.enterpriseSelectedRowKeys = selectedRowKeys
    },
    // 代理商相关方法
    getAgentList() {
      this.agentLoading = true
      fetchAgentList(this.agentQueryParam)
        .then((res) => {
          this.agentList = res.data.records || []
          this.agentPagination.total = res.data.total || 0
          this.agentPagination.current = this.agentQueryParam.pageNum
        })
        .finally(() => {
          this.agentLoading = false
        })
    },
    handleAgentQuery() {
      this.agentQueryParam.pageNum = 1
      this.agentPagination.current = 1
      this.getAgentList()
    },
    resetAgentQuery() {
      // 重置为代理商的基础查询参数，保留必要的type和status
      this.agentQueryParam = { ...BASE_AGENT_QUERY }
      this.agentPagination.current = 1
      this.getAgentList()
    },
    handleAgentTableChange(pagination) {
      this.agentQueryParam.pageNum = pagination.current
      this.agentQueryParam.pageSize = pagination.pageSize
      this.agentPagination = {
        ...this.agentPagination,
        current: pagination.current,
        pageSize: pagination.pageSize,
      }
      this.getAgentList()
    },
    handleAgentSelectionChange(selectedRowKeys) {
      this.agentSelectedRowKeys = selectedRowKeys
    },
    // 提交发放
    submit() {
      if (!this.canSubmit) {
        if (this.selectedUserCount === 0) {
          this.$message.warning('请选择要发放的用户')
        } else if (this.distributeCount <= 0) {
          this.$message.warning('请输入正确的发放数量')
        }
        return
      }

      // 确认对话框
      this.$confirm({
        title: '确认发放优惠券',
        content: `您确定要向 ${this.selectedUserCount} 个用户发放 ${this.totalDistributionCount} 张优惠券吗？`,
        onOk: () => {
          this.doDistribute()
        },
      })
    },
    // 执行发放操作
    doDistribute() {
      this.submitting = true
      distributeCoupon({
        couponId: this.couponId,
        objIds: this.selectedUserIds,
        count: this.distributeCount,
      })
        .then(() => {
          this.$message.success(`成功发放 ${this.totalDistributionCount} 张优惠券`)
          this.$emit('success')
          this.close()
        })
        .catch((error) => {
          console.error('发放失败:', error)
          this.$message.error('发放失败')
        })
        .finally(() => {
          this.submitting = false
        })
    },
  },
}
</script>

<style scoped>
.drawer-footer {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 100%;
  border-top: 1px solid #e9e9e9;
  padding: 10px 16px;
  background: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1;
}

.footer-left {
  flex: 1;
}

.footer-right {
  flex-shrink: 0;
}

.distribute-config {
  padding: 16px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}

.distribution-hint {
  color: #666;
  font-size: 12px;
  margin-left: 16px;
}

.distribution-summary {
  display: flex;
  gap: 16px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-item .label {
  color: #666;
  font-size: 14px;
}

.summary-item .value {
  color: #1890ff;
  font-weight: 500;
  font-size: 14px;
}
</style>
